/**
 * Defera Capital — checkout status page (post-checkout recovery).
 *
 * Shown to customers when their checkout drops between "Stripe charged the card"
 * and "Defera created the booking row." The most common path is the inline
 * recovery built into `DeferaZeroCheckoutForm` (it polls + redirects without
 * ever leaving the checkout). This page exists for the long tail:
 *   - Customer closed the tab mid-recovery.
 *   - Customer hit reload and lost in-memory state.
 *   - Support sends them a deep link with their reference.
 *
 * The page polls `defera-capital/checkout-status` and:
 *   - On `confirmed` → redirects to /dashboard with the success flag.
 *   - On `processing` → keeps polling (deposit charged, webhook pending).
 *   - On `failed` → says the booking didn't go through and nothing was charged
 *     (the server reports it only when the deposit never reached a payment).
 *   - On `pending` (stale) / `not_found` → shows a calm explainer + support CTA.
 *   - On poll timeout → switches to a "still waiting" state with a manual refresh
 *     button so the customer never gets stuck on a permanent spinner.
 *
 * Industry-standard pattern: read-only status query + non-blocking poll, never
 * makes the customer wonder if their money is gone.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Loader2, CheckCircle2, AlertCircle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/lib/supabase';
import { createPageUrl } from '@/utils';
import {
  awaitCapitalCheckoutConfirmed,
  deriveCapitalTripReference,
} from '@/lib/awaitCapitalCheckout';
import { deferaCapitalNmiUrl } from '@/lib/apiUrls';

/** Auto-redirect delay after we see `confirmed` so the customer can read the message briefly. */
const CONFIRMED_REDIRECT_DELAY_MS = 1500;

export default function CapitalCheckoutStatus() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  /** Accept either ?ref=YZ1234 (short) or ?trip_reference=YZ1234 (matches API). */
  const queryRef = (searchParams.get('ref') || searchParams.get('trip_reference') || '').trim().toUpperCase();
  /** Allow ?flight_id=offer_xyz as a fallback so deep links from emails work without the
   *  customer needing to know the reference (we derive it the same way the server does). */
  const flightId = searchParams.get('flight_id') || '';
  const tripReference = useMemo(() => {
    if (queryRef) return queryRef;
    return deriveCapitalTripReference(flightId);
  }, [queryRef, flightId]);
  /** Processor tag attached by the form that redirected here. When the customer's
   *  checkout dropped between charge and booking, the form (Stripe or NMI) appends
   *  `?processor=nmi` (or omits it for Stripe). Without this hint the recovery page
   *  would always poll the Stripe endpoint and miss NMI deposits sitting in
   *  `defera_capital_nmi_pending_checkouts`. */
  const processorHint = (searchParams.get('processor') || '').toLowerCase() === 'nmi' ? 'nmi' : 'stripe';

  const [status, setStatus] = useState('initializing');
  const [latestPayload, setLatestPayload] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  /** Increments every time the user clicks "Check again" so the polling effect re-runs. */
  const [refreshKey, setRefreshKey] = useState(0);
  const abortRef = useRef(null);

  useEffect(() => {
    if (!tripReference) {
      setStatus('missing_reference');
      return undefined;
    }

    let cancelled = false;
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          if (!cancelled) {
            setStatus('not_signed_in');
          }
          return;
        }
        if (cancelled) return;
        setStatus('polling');

        const result = await awaitCapitalCheckoutConfirmed({
          tripReference,
          accessToken: session.access_token,
          options: {
            timeoutMs: 60_000,
            signal: ctrl.signal,
            /** NMI track snapshots live in a different table — point the poller
             *  at the NMI status endpoint when the redirect carried the
             *  `processor=nmi` hint. Without the override the poller would only
             *  see Stripe pendings. */
            statusUrl: processorHint === 'nmi' ? deferaCapitalNmiUrl('checkout-status') : undefined,
            onProgress: (s, payload) => {
              if (cancelled) return;
              setLatestPayload(payload);
              setStatus(s);
            },
          },
        });
        if (cancelled) return;

        setLatestPayload(result);
        setStatus(result.status);

        if (result.status === 'confirmed') {
          /** Brief pause so the customer registers the success state before we navigate. */
          setTimeout(() => {
            if (!cancelled) {
              window.location.href = createPageUrl('Dashboard') + '?defera_capital=success';
            }
          }, CONFIRMED_REDIRECT_DELAY_MS);
        }
      } catch (e) {
        if (cancelled) return;
        console.error('CapitalCheckoutStatus poll error', e);
        setErrorMessage(e?.message || 'Could not check checkout status.');
        setStatus('error');
      }
    })();

    return () => {
      cancelled = true;
      try { ctrl.abort(); } catch { /* ignore */ }
    };
  }, [tripReference, refreshKey, processorHint]);

  const onCheckAgain = () => {
    setErrorMessage(null);
    setRefreshKey((k) => k + 1);
  };

  const headerCopy = (() => {
    switch (status) {
      case 'confirmed':
        return { icon: <CheckCircle2 className="w-12 h-12 text-emerald-400" />, title: 'Booking confirmed', subtitle: 'Redirecting you to My Trips…' };
      case 'processing':
        return { icon: <Loader2 className="w-12 h-12 text-brand-400 animate-spin" />, title: 'Finalizing your booking', subtitle: 'Stripe confirmed your deposit. We\'re creating your trip now — usually takes a few seconds.' };
      case 'polling':
      case 'pending':
      case 'initializing':
        return { icon: <Loader2 className="w-12 h-12 text-brand-400 animate-spin" />, title: 'Confirming your payment', subtitle: 'We\'re checking with our system.' };
      case 'timeout':
        return { icon: <AlertCircle className="w-12 h-12 text-brand-400" />, title: 'Still waiting on confirmation', subtitle: 'This is taking longer than usual. You can check again or reach out — we\'ll find your trip.' };
      case 'failed':
        return { icon: <AlertCircle className="w-12 h-12 text-white/60" />, title: 'This booking didn\'t go through', subtitle: 'Your card wasn\'t charged and no trip was created. You can search again and book it.' };
      case 'not_found':
        return { icon: <AlertCircle className="w-12 h-12 text-white/60" />, title: 'No checkout found', subtitle: 'We couldn\'t find a checkout for that reference. If you were just charged, please email support.' };
      case 'missing_reference':
        return { icon: <AlertCircle className="w-12 h-12 text-white/60" />, title: 'Missing trip reference', subtitle: 'This page needs a trip reference (added as ?ref=… in the URL).' };
      case 'not_signed_in':
        return { icon: <AlertCircle className="w-12 h-12 text-white/60" />, title: 'Please sign in', subtitle: 'You need to be signed in to check your checkout status.' };
      case 'error':
      default:
        return { icon: <AlertCircle className="w-12 h-12 text-red-400" />, title: 'Something went wrong', subtitle: errorMessage || 'We couldn\'t check your checkout status.' };
    }
  })();

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-4 py-16">
      <div className="max-w-md w-full bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-8 text-center">
        <div className="flex flex-col items-center gap-4">
          {headerCopy.icon}
          <h1 className="text-2xl font-light text-white">{headerCopy.title}</h1>
          <p className="text-white/60 font-normal">{headerCopy.subtitle}</p>

          {/* No reference on a failed checkout: beside "no trip was created" it reads like a booking number. */}
          {tripReference && status !== 'failed' && (
            <p className="text-xs text-white/40 font-mono mt-2">Reference: {tripReference}</p>
          )}

          {(status === 'pending' || status === 'timeout' || status === 'error' || status === 'not_found') && (
            <div className="flex flex-col gap-3 w-full mt-4">
              <Button
                type="button"
                onClick={onCheckAgain}
                disabled={!tripReference}
                className="w-full"
                variant="secondary"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Check again
              </Button>
              <Link
                to="/dashboard"
                className="text-sm text-white/60 hover:text-white transition-colors"
              >
                Open My Trips
              </Link>
              <Link
                to="/contact#message"
                className="text-sm text-brand-400/90 hover:text-brand-400 transition-colors"
              >
                Send us a message
              </Link>
            </div>
          )}

          {status === 'failed' && (
            <div className="flex flex-col gap-3 w-full mt-4">
              <Button
                type="button"
                onClick={() => navigate('/home')}
                className="w-full"
                variant="secondary"
              >
                Search flights
              </Button>
              <Link
                to="/dashboard"
                className="text-sm text-white/60 hover:text-white transition-colors"
              >
                Open My Trips
              </Link>
              <Link
                to="/contact#message"
                className="text-sm text-brand-400/90 hover:text-brand-400 transition-colors"
              >
                Send us a message
              </Link>
            </div>
          )}

          {status === 'not_signed_in' && (
            <div className="flex flex-col gap-3 w-full mt-4">
              <Button
                type="button"
                onClick={() => navigate('/home')}
                className="w-full"
                variant="secondary"
              >
                Sign in
              </Button>
            </div>
          )}

          {latestPayload?.deposit_paid_at && (
            <p className="text-xs text-emerald-400/80 mt-3">
              Deposit recorded {new Date(latestPayload.deposit_paid_at).toLocaleTimeString()}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
